import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import LineItemBudgetPreview from '../../../../ReusableFormSections/reusableBudgetSections/FacebookBudgetSections/LineItemBudgetPreview'
import LineItemsBudgetSectionForm from './LineItemBudgetSection/LineItemsBudgetSectionForm'
import CampaignBudgetSectionPreview from '../../../../ReusableFormSections/reusableBudgetSections/FacebookBudgetSections/FacebookCampaignBudgetSection/CampaignBudgetSectionPreview'
import CampaignBudgetSectionForm from '../../../../ReusableFormSections/reusableBudgetSections/FacebookBudgetSections/FacebookCampaignBudgetSection/CampaignBudgetSectionForm'
import RemainingCampaignBudgetFields from './CampaignBudgetSection/RemainingCampaignBudgetFields'
import CampaignImpressionBudgetForm from './CampaignImpressionsBudgetSection/CampaignImpressionBudgetForm'
import CampaignImpressionsBudgetPreview from './CampaignImpressionsBudgetSection/campaignImpressionsBudgetPreview'
import LineItemsImpressionsBudgetForm from './LineItemsImpressionsBudgetSection/LineItemsImpressionsBudgetForm'
import LineItemImpressionsBudgetPreview from './LineItemsImpressionsBudgetSection/LineItemsImpressionsBudgetPreview'

import { combinedCampaignsSelector, combinedLineItemsSelector } from '../../../../../modules/selectors/combinedData'
import { UPDATE_COMBINED_CAMPAIGN } from '../../../../../modules/actions/combinedData'
import { rightSelector } from '../../../../../modules/selectors/rights'

import useRightBudgetAndImpressionsCalculation from '../../../UploadAdCreative/useRightBudgetAndImpressionsCalculation'
import { insertIf } from '../../../../../helpers/common'

import { BUDGET_DAILY, BUDGET_LIFETIME, CAMPAIGN_BUDGET } from '../../../CampaignForms/fields'
import {
  BUDGET_DAILY_LINE_ITEM,
  BUDGET_LIFETIME_LINE_ITEM
} from '../../../CombinedFacebookTiktokCreate/CreateFormSelector/CampaignCreateForm/fields'
import { FACEBOOK_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { LINE_ITEM_BUDGET } from '../../../../Facebook/LineItemForms/LineItemFacebookEdit/fields'
import {
  DIGITAL_ALLOCATION_TYPE_BUDGET,
  DIGITAL_ALLOCATION_TYPE_IMPRESSIONS
} from '../../../Partnerships/RightForms/fields'

export const useFacebookCampaignBudgetEditSections = selectedEditItemId => {
  const combinedCampaigns = useSelector(combinedCampaignsSelector)
  const combinedLineItemsGroupedByCampaigns = useSelector(combinedLineItemsSelector)
  const { remainingBudget, remainingImpressions } = useRightBudgetAndImpressionsCalculation()

  const { allocation_type: rightAllocationType } = useSelector(rightSelector)
  const isBudgetAllocationType = rightAllocationType === DIGITAL_ALLOCATION_TYPE_BUDGET
  const isImpressionAllocationType = rightAllocationType === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS

  const { campaigns = [] } = combinedCampaigns
  const selectedCampaign = campaigns.find(campaign => String(campaign.id) === String(selectedEditItemId))

  const selectedCombinedLineItemsCampaign =
    combinedLineItemsGroupedByCampaigns.find(({ campaign_id }) => String(campaign_id) === String(selectedEditItemId)) ||
    {}

  const { line_items: combinedLineItems = [] } = selectedCombinedLineItemsCampaign

  const {
    [BUDGET_LIFETIME]: budgetLifetime,
    [BUDGET_DAILY]: budgetDaily,
    [BUDGET_LIFETIME_LINE_ITEM]: budgetLifetimeLineItem,
    [BUDGET_DAILY_LINE_ITEM]: budgetDailyLineItem,
    ad_account_id: adAccountId
  } = selectedCampaign || {}

  const isCampaignLevelBudget = !!budgetLifetime || !!budgetDaily
  const isLineItemLevelBudget = !!budgetLifetimeLineItem || !!budgetDailyLineItem

  const sectionsInitialOpenStates = {
    ...(isCampaignLevelBudget ? { [CAMPAIGN_BUDGET]: true } : {})
  }

  const lineItemsBudgetSections = useMemo(() => {
    return combinedLineItems.map(lineItem => {
      return {
        sectionName: `${LINE_ITEM_BUDGET}_${lineItem.id}`,
        title: `${lineItem.name}`,
        PreviewComponent: <LineItemBudgetPreview lineItem={lineItem} />,
        FormComponent: (
          <LineItemsBudgetSectionForm
            adAccountId={adAccountId}
            campaign={selectedCampaign}
            lineItem={lineItem}
            remainingBudget={remainingBudget}
          />
        )
      }
    })
  }, [combinedLineItems, selectedCampaign, remainingBudget, adAccountId])

  const lineItemsImpressionsBudgetSections = useMemo(() => {
    return combinedLineItems.map(lineItem => {
      return {
        sectionName: `${LINE_ITEM_BUDGET}_${lineItem.id}`,
        title: `${lineItem.name}`,
        PreviewComponent: <LineItemImpressionsBudgetPreview lineItem={lineItem} />,
        FormComponent: (
          <LineItemsImpressionsBudgetForm
            adAccountId={adAccountId}
            campaign={selectedCampaign}
            lineItem={lineItem}
            remainingImpressions={remainingImpressions}
          />
        )
      }
    })
  }, [combinedLineItems, selectedCampaign, remainingImpressions, adAccountId])

  const campaignBudgetSection = useMemo(
    () => ({
      sectionName: CAMPAIGN_BUDGET,
      title: 'Budget',
      PreviewComponent: <CampaignBudgetSectionPreview campaign={selectedCampaign} />,
      FormComponent: (
        <CampaignBudgetSectionForm
          platform={FACEBOOK_PLATFORM}
          campaign={selectedCampaign}
          adAccountId={adAccountId}
          CampaignBudgetFormFields={RemainingCampaignBudgetFields}
          remainingBudget={remainingBudget}
          updateOptions={{
            updateType: UPDATE_COMBINED_CAMPAIGN
          }}
        />
      )
    }),
    [selectedCampaign, remainingBudget, adAccountId]
  )

  const campaignImpressionsBudgetSection = useMemo(() => {
    return {
      sectionName: 'campaign_impression_budget',
      title: 'Impression',
      PreviewComponent: <CampaignImpressionsBudgetPreview campaign={selectedCampaign} />,
      FormComponent: (
        <CampaignImpressionBudgetForm
          platform={FACEBOOK_PLATFORM}
          campaign={selectedCampaign}
          adAccountId={adAccountId}
          remainingImpressions={remainingImpressions}
          updateOptions={{
            updateType: UPDATE_COMBINED_CAMPAIGN
          }}
        />
      )
    }
  }, [remainingImpressions, selectedCampaign, adAccountId])

  const impressionsAllocationSections = useMemo(
    () => [
      // campaign impression budget section
      ...insertIf(isCampaignLevelBudget, campaignImpressionsBudgetSection),
      // lineItem impression budget sections
      ...insertIf(isLineItemLevelBudget, ...lineItemsImpressionsBudgetSections)
    ],
    [isLineItemLevelBudget, lineItemsImpressionsBudgetSections, isCampaignLevelBudget, campaignImpressionsBudgetSection]
  )

  const budgetAllocationSections = useMemo(
    () => [
      // campaign budget section,
      ...insertIf(isCampaignLevelBudget, campaignBudgetSection),
      // lineItem budget sections
      ...insertIf(isLineItemLevelBudget, ...lineItemsBudgetSections)
    ],
    [isLineItemLevelBudget, lineItemsBudgetSections, isCampaignLevelBudget, campaignBudgetSection]
  )

  const editSections = useMemo(() => {
    return [
      ...(isBudgetAllocationType ? budgetAllocationSections : []),
      ...(isImpressionAllocationType ? impressionsAllocationSections : [])
    ]
  }, [isBudgetAllocationType, isImpressionAllocationType, budgetAllocationSections, impressionsAllocationSections])

  return { editSections, sectionsInitialOpenStates }
}
